import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider, Paper, Stack, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import dayjs, { Dayjs } from 'dayjs';

import AppButton from '@/shared/ui/AppButton';

import CalendarDatePickerDay from './CalendarDatePickerDay';
import CalendarDatePickerHeader from './CalendarDatePickerHeader';
import UnavailableLegend from './UnavailableLegend';
import { dayCalendarSx } from './dayCalendarGridSx';

export interface CalendarDatePickerProps {
  value: Dayjs | null;
  onChange: (value: Dayjs) => void;
  isDateUnavailable?: (date: Dayjs) => boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disablePast?: boolean;
  hideUnavailableLegend?: boolean;
  sx?: SxProps<Theme>;
  onClear?: () => void;
  'data-testid'?: string;
}

const CalendarDatePicker: FC<CalendarDatePickerProps> = ({
  value,
  onChange,
  isDateUnavailable,
  minDate,
  maxDate,
  disablePast,
  hideUnavailableLegend,
  sx,
  onClear,
  'data-testid': dataTestId = 'calendar-date-picker',
}) => {
  const { t } = useTranslation();
  const isViewingToday = value?.isToday() ?? false;

  return (
    <Paper
      elevation={0}
      data-testid={dataTestId}
      sx={[
        {
          backgroundColor: '#FEFEFE',
          boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.15)',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: 'fit-content',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        sx={{
          height: '40px',
          border: '1px solid rgba(0, 0, 0, 0.15)',
          borderRadius: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '12px 16px',
        }}
      >
        <Stack direction="row" alignItems="center" gap="8px">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.6667 0.833496C10.9428 0.833496 11.1667 1.05735 11.1667 1.3335V2.16683H12.6667C13.6792 2.16683 14.5 2.98764 14.5 4.00016V13.3335C14.5 14.346 13.6792 15.1668 12.6667 15.1668H3.33333C2.32081 15.1668 1.5 14.346 1.5 13.3335V4.00016C1.5 2.98764 2.32081 2.16683 3.33333 2.16683H4.83333V1.3335C4.83333 1.05735 5.05719 0.833496 5.33333 0.833496C5.60948 0.833496 5.83333 1.05735 5.83333 1.3335V2.16683H10.1667V1.3335C10.1667 1.05735 10.3905 0.833496 10.6667 0.833496ZM2.5 7.16683V13.3335C2.5 13.7937 2.8731 14.1668 3.33333 14.1668H12.6667C13.1269 14.1668 13.5 13.7937 13.5 13.3335V7.16683H2.5ZM3.33333 3.16683C2.8731 3.16683 2.5 3.53993 2.5 4.00016V6.16683H13.5V4.00016C13.5 3.53993 13.1269 3.16683 12.6667 3.16683H11.1667V4.00016C11.1667 4.2763 10.9428 4.50016 10.6667 4.50016C10.3905 4.50016 10.1667 4.2763 10.1667 4.00016V3.16683H5.83333V4.00016C5.83333 4.2763 5.60948 4.50016 5.33333 4.50016C5.05719 4.50016 4.83333 4.2763 4.83333 4.00016V3.16683H3.33333Z"
              fill="#A4A4B6"
            />
          </svg>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: 1.3,
              color: (theme) => (value ? theme.palette.grey[900] : theme.palette.grey[400]),
            }}
          >
            {value ? value.format('MMM D, YYYY') : t('pick_a_day')}
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.3,
            color: (theme) => theme.palette.grey[900],
          }}
        >
          {value ? value.format('dddd') : ''}
        </Typography>
      </Stack>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          value={value}
          onChange={(newValue) => newValue && onChange(newValue)}
          views={['day']}
          minDate={minDate}
          maxDate={maxDate}
          disablePast={disablePast}
          reduceAnimations
          dayOfWeekFormatter={(date) => date.format('dd')}
          shouldDisableDate={isDateUnavailable}
          slots={{
            calendarHeader: CalendarDatePickerHeader,
            day: (dayProps) => (
              <CalendarDatePickerDay {...dayProps} isDateUnavailable={isDateUnavailable} />
            ),
          }}
          sx={dayCalendarSx}
        />
      </LocalizationProvider>

      {!hideUnavailableLegend && <UnavailableLegend />}

      <Divider sx={{ borderColor: (theme) => theme.palette.grey[100] }} />

      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px">
        {!!onClear && (
          <AppButton
            variant="link"
            size="small"
            onClick={onClear}
            sx={{
              fontSize: '12px',
              lineHeight: 1.25,
              color: (theme) => (value ? theme.palette.accent.main : theme.palette.grey[400]),
              paddingBottom: '2px !important',
              borderRadius: 0,
              borderBottom: (theme) =>
                `1px solid ${value ? theme.palette.accent.main : theme.palette.grey[400]}`,
              '&:hover': {
                color: (theme) => (value ? theme.palette.accent.main : theme.palette.grey[400]),
              },
            }}
            data-testid={`${dataTestId}-clear`}
          >
            {t('clear')}
          </AppButton>
        )}
        <AppButton
          variant="link"
          size="small"
          onClick={() => onChange(dayjs())}
          sx={{
            fontSize: '12px',
            lineHeight: 1.25,
            color: (theme) =>
              isViewingToday ? theme.palette.grey[400] : theme.palette.accent.main,
            paddingBottom: '2px !important',
            borderRadius: 0,
            borderBottom: (theme) =>
              `1px solid ${isViewingToday ? theme.palette.grey[400] : theme.palette.accent.main}`,
            '&:hover': {
              color: (theme) =>
                isViewingToday ? theme.palette.grey[400] : theme.palette.accent.main,
            },
          }}
          data-testid={`${dataTestId}-today`}
        >
          {t('today')}
        </AppButton>
      </Stack>
    </Paper>
  );
};

export default CalendarDatePicker;
